/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ModalNestedPicker } from '../../shared/modals/ModalNestedPicker';
import { SettingsNavigationRow } from '../../settings/components/SettingsNavigationRow';
import { spacing } from '../../shared/theme/spacing';
import { typography } from '../../shared/theme/typography';
import { ticketColors } from '../../shared/theme/ticketColors';
import {
  handleLibroIvaComprasCsvExport,
  handleLibroIvaComprasTxtExport,
  handleLibroIvaControlPdfExport,
  handleLibroIvaExport,
  handleLibroIvaTxtExport,
} from '../application/libroIvaExport';

const MONTH_NAMES = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre',
];

export type LibroIvaExportSectionProps = {
  branchId: string;
};

/**
 * Export Libro IVA Digital: selector de período mensual + 2 TXT oficiales
 * (VENTAS_CBTE / VENTAS_ALICUOTAS). El CSV simplificado queda como opción
 * secundaria para lectura rápida.
 */
export function LibroIvaExportSection({ branchId }: LibroIvaExportSectionProps) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const now = new Date();
  const [period, setPeriod] = useState<{ year: number; month: number }>({
    year: now.getFullYear(),
    month: now.getMonth() + 1,
  });

  const periodOptions = useMemo(() => {
    const options: Array<{ value: string; label: string }> = [];
    const cursor = new Date(now.getFullYear(), now.getMonth(), 1);
    for (let i = 0; i < 24; i += 1) {
      const y = cursor.getFullYear();
      const m = cursor.getMonth() + 1;
      options.push({
        value: `${y}-${m}`,
        label: `${MONTH_NAMES[m - 1]} ${y}`,
      });
      cursor.setMonth(cursor.getMonth() - 1);
    }
    return options;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const periodLabel = `${MONTH_NAMES[period.month - 1]} ${period.year}`;

  return (
    <View>
      <Text style={styles.fieldLabel}>Libro IVA Digital</Text>
      <TouchableOpacity
        style={styles.selectTrigger}
        onPress={() => setPickerOpen(true)}
        accessibilityRole="button"
        accessibilityLabel={`Período: ${periodLabel}. Abrir lista`}
        testID="fiscal.status.period-trigger"
      >
        <Text style={styles.selectValue} numberOfLines={1}>
          {periodLabel}
        </Text>
        <Ionicons name="chevron-down" size={18} color={ticketColors.ink2} />
      </TouchableOpacity>
      <SettingsNavigationRow
        iconName="download-outline"
        title="Exportar ventas ARCA (TXT)"
        description="Comprobantes y alícuotas de ventas del mes, formato oficial."
        onPress={() => {
          void handleLibroIvaTxtExport(branchId, period.year, period.month);
        }}
        showSeparator
        testID="fiscal.status.export-iva-txt"
      />
      <SettingsNavigationRow
        iconName="download-outline"
        title="Exportar compras ARCA (TXT)"
        description="Comprobantes fiscales recibidos del mes, formato oficial."
        onPress={() => {
          void handleLibroIvaComprasTxtExport(branchId, period.year, period.month);
        }}
        showSeparator
        testID="fiscal.status.export-iva-compras-txt"
      />
      <SettingsNavigationRow
        iconName="document-text-outline"
        title="Exportar resumen ventas (CSV)"
        description="Ventas fiscales del año para Excel o Google Sheets."
        onPress={() => {
          void handleLibroIvaExport(branchId);
        }}
        showSeparator
        testID="fiscal.status.export-iva"
      />
      <SettingsNavigationRow
        iconName="document-text-outline"
        title="Exportar resumen compras (CSV)"
        description="Compras con datos fiscales del mes, listo para planilla."
        onPress={() => {
          void handleLibroIvaComprasCsvExport(branchId, period.year, period.month);
        }}
        showSeparator
        testID="fiscal.status.export-iva-compras-csv"
      />
      <SettingsNavigationRow
        iconName="document-outline"
        title="Exportar control (PDF)"
        description="Ventas y compras del mes en un PDF para revisar o mandar al contador."
        onPress={() => {
          void handleLibroIvaControlPdfExport(branchId, period.year, period.month);
        }}
        showSeparator={false}
        testID="fiscal.status.export-iva-control-pdf"
      />

      <ModalNestedPicker
        visible={pickerOpen}
        title="Período del libro IVA"
        options={periodOptions}
        selectedValue={`${period.year}-${period.month}`}
        onSelect={(value) => {
          const [y, m] = value.split('-').map(Number);
          if (y && m) setPeriod({ year: y, month: m });
          setPickerOpen(false);
        }}
        onClose={() => setPickerOpen(false)}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  fieldLabel: {
    ...typography.caption,
    color: ticketColors.ink2,
    textTransform: 'uppercase',
    letterSpacing: 0.8,
    fontWeight: '700',
    marginBottom: spacing.xs,
  },
  selectTrigger: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    minHeight: 44,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.xs,
  },
  selectValue: {
    ...typography.body,
    flex: 1,
    minWidth: 0,
    color: ticketColors.ink,
    fontWeight: '600',
  },
});
